import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { MenuItem, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import { RHFSelect, TruncatedTypography } from '@app/components';
import { TableCellCustom } from '@app/components/table';
import { Strings } from '@app/constants';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { CustomAvatar, CustomAvatarGroup } from '@app/components/custom-avatar';
import { StatusTooltipChips } from '@app/components/status-tag-tooltip';
import { useRequestState } from '@app/hooks';
import { IList, IMembers, ITasks } from '../type';
import { updateTaskStatus } from '../slice';
import { selectUpdateTaskStatusState } from '../selector';

interface Props {
  row: ITasks;
}

const TasksRow = (props: Props) => {
  const { row } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const foundList = row?.lists?.find((data: IList) => row.listId === data.listId);

  useRequestState({
    stateSelector: selectUpdateTaskStatusState,
    successMessageShown: true,
  });

  const methods = useForm({
    defaultValues: {
      updateStatus: foundList?.listName,
    },
  });
  const updatedStatus = useWatch({ control: methods.control, name: 'updateStatus' });

  const handleDetails = () => {
    navigate(PATH_DASHBOARD.taskDetail, { state: { row } });
  };

  return (
    <>
      <TableCellCustom align="center" onClick={handleDetails}>
        <Typography width={120} variant="body2">
          {row?.projectName ?? 'NA'}
        </Typography>
      </TableCellCustom>
      <TableCellCustom align="center" onClick={handleDetails}>
        <Typography width={120} variant="body2">
          {row?.boardName ?? 'NA'}
        </Typography>
      </TableCellCustom>
      <TableCellCustom align="center" onClick={handleDetails}>
        <TruncatedTypography text={row?.taskName} width={120} />
      </TableCellCustom>
      <TableCellCustom flex onClick={handleDetails}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CustomAvatar
            name={`${row?.user?.firstName} ${row?.user?.lastName}`}
            color="info"
            src={row?.user?.profileImage}
          />
          <Typography variant="body2" width={100}>
            {row?.assignedBy ?? 'NA'}
          </Typography>
        </Stack>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        {Array.isArray(row?.members) && row.members.length > 0 ? (
          <CustomAvatarGroup max={5}>
            {row.members.map((member: IMembers, index: number) => (
              <CustomAvatar
                key={index}
                name={`${member.firstName} ${member.lastName}`}
                color="info"
                src={member?.avatarUrl}
              />
            ))}
          </CustomAvatarGroup>
        ) : (
          <Typography variant="body2" color="textSecondary">
            N/A
          </Typography>
        )}
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <Typography variant="body2" width={80}>
          {row?.dueDates?.startDate ? dayjs(row?.dueDates?.startDate).format('DD-MM-YYYY') : 'N/A'}{' '}
          {row?.dueDates?.startTime ? dayjs(row?.dueDates?.startTime).format('HH:mm') : ''}
        </Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <Typography variant="body2" width={80}>
          {row?.dueDates?.endDate ? dayjs(row?.dueDates?.endDate).format('DD-MM-YYYY') : 'N/A'}{' '}
          {row?.dueDates?.endTime ? dayjs(row?.dueDates?.endTime).format('HH:mm') : ''}
        </Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <Typography variant="body2" width={80}>
          {row?.dueDates?.isCompletedTime
            ? dayjs(row.dueDates.isCompletedTime).format('DD MMM YYYY, hh:mm A')
            : 'NA'}
        </Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <Typography variant="body2" width={80}>
          {row?.actualEta ?? 'NA'}
        </Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        <Typography variant="body2" width={80}>
          {row?.expectedEta ?? 'NA'}
        </Typography>
      </TableCellCustom>
      <TableCellCustom flex align="center" onClick={handleDetails}>
        {row?.labels && row?.labels.length > 0 ? (
          <StatusTooltipChips tagData={row?.labels} />
        ) : (
          <TruncatedTypography text={Strings.common.na} width={80} />
        )}
      </TableCellCustom>
      <TableCellCustom flex align="center">
        <FormProvider {...methods}>
          <RHFSelect
            name="updateStatus"
            value={updatedStatus}
            sx={{ width: '120px' }}
            SelectProps={{
              sx: {
                fontSize: '13px',
              },
            }}
            size="small"
            onChange={(e) => {
              const selectedListId = row?.lists.find(
                (option) => option.listName === e.target.value
              )?.listId;
              methods.setValue('updateStatus', e.target.value);
              dispatch(
                updateTaskStatus({
                  boardId: row?.boardId,
                  existingListId: row?.listId,
                  cardId: row?.id,
                  newListId: selectedListId,
                })
              );
            }}
          >
            {row?.lists?.map((option: IList) => (
              <MenuItem key={option.listName} value={option.listName} sx={{ fontSize: '13px' }}>
                {option.listName}
              </MenuItem>
            ))}
          </RHFSelect>
        </FormProvider>
      </TableCellCustom>
    </>
  );
};

export { TasksRow };
